import { Component, Input, OnInit } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { LinkType } from '../../core/enum/link-type.enum';
import { TncModalComponent } from '../../pages/signup/tnc-modal/tnc-modal.component';
import { environment } from '../../../environments/environment';
import { APP_VERSION, DefaultEmailPattern } from '../../core/services/constant';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AccountService } from '../../core/services/api/account.service';
import { ErrorService } from '../../core/services/error.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent implements OnInit {
  @Input() isExpandFooter: boolean;
  footerItems = [
    {
      colTitle: 'company',
      items: [
        { title: 'pitchIN', linkType: LinkType.RouterLink, url: '/about-us' },
        {
          title: 'businessDirectory',
          linkType: LinkType.RouterLink,
          url: '/businesses/details',
        },
        //{ title: "theTeam", linkType: LinkType.RouterLink, url: '/about-us', fragment: "our-team" },
        //{ title: "partnership", linkType: LinkType.RouterLink, url: '/about-us', fragment: "our-partners" },
        {
          title: 'contactUs',
          linkType: LinkType.URL,
          url: 'https://support.pitchin.my/hc/en-us/requests/new',
          badge: 'emailUs',
          badgeIcon: 'fa fa-envelope-o',
          badgeAction: {
            linkType: LinkType.URL,
            url: 'mailto:support@pitchin.my',
          },
        },
        {
          title: 'careers',
          linkType: LinkType.URL,
          url: 'https://careers.pitchin.my',
        },
        {
          title: 'brandGuidelines',
          linkType: LinkType.RouterLink,
          url: '/brand-guidelines',
        },
        {
          title: 'equityReport2020',
          linkType: LinkType.URL,
          url: 'https://res.cloudinary.com/pitchin/image/upload/ecf/reports/pitchIN_Report_2020-v1.pdf',
        },
      ],
    },
    {
      colTitle: 'learn',
      items: [
        {
          title: 'learningPortal',
          linkType: LinkType.RouterLink,
          url: '/learn',
        },
        {
          title: 'faqs',
          linkType: LinkType.URL,
          url: 'https://support.pitchin.my/hc/en-us',
        },
        {
          title: 'academy',
          linkType: LinkType.URL,
          url: 'https://academy.pitchin.my',
        },
        //{ title: "investing&EquityCrowdfunding", linkType: LinkType.URL, url: 'https://support.pitchin.my/hc/en-us/sections/4413638279697-INVESTING-IN-AN-EQUITY-CROWDFUNDING-ECF-CAMPAIGN' },
        {
          title: 'feeStructure',
          linkType: LinkType.RouterLink,
          url: '/fee-structure',
        },
        {
          title: 'equityCrowdfunding',
          linkType: LinkType.RouterLink,
          url: '/learn/invest/how-it-works',
        },
        {
          title: 'secondaryMarket',
          linkType: LinkType.RouterLink,
          url: '/learn/trade/about-pitchin-secondary-trading-market',
        },
      ],
    },
    {
      colTitle: 'legalInfo',
      items: [
        {
          title: 'websiteTermsConditions',
          linkType: LinkType.RouterLink,
          url: 'website-terms-conditions',
        },
        {
          title: 'serviceTermsConditions',
          linkType: LinkType.RouterLink,
          url: 'service-terms-conditions',
        },
        {
          title: 'ieoTermsConditions',
          linkType: LinkType.RouterLink,
          url: 'ieo-terms-conditions',
        },
        {
          title: 'pstxTermsConditions',
          linkType: LinkType.RouterLink,
          url: 'pstx-terms-conditions',
        },
        {
          title: 'nomineeAgreement',
          linkType: LinkType.RouterLink,
          url: 'nominee-agreement',
        },
        {
          title: 'privacyPolicy',
          linkType: LinkType.RouterLink,
          url: 'privacy-policy',
        },
        {
          title: 'pdpaPolicy',
          linkType: LinkType.RouterLink,
          url: 'pdpa-policy',
        },
        {
          title: 'riskWarning',
          linkType: LinkType.RouterLink,
          url: 'risk-warning',
        },
        // P2P-28 · F0.3 — links to the consolidated P2P sections. Hidden until the P2P-29
        // notesEnabled flag is on, since the target sections ship dark behind it.
        {
          title: 'notesP2PTerms',
          linkType: LinkType.RouterLink,
          url: 'website-terms-conditions',
          fragment: 'notes-p2p',
          show: () => this.featureFlagService.notesEnabled,
        },
        {
          title: 'notesP2PRiskWarning',
          linkType: LinkType.RouterLink,
          url: 'risk-warning',
          fragment: 'note',
          show: () => this.featureFlagService.notesEnabled,
        },
      ],
    },
  ];
  currentYear = new Date().getFullYear();
  LinkType = LinkType;
  Version = APP_VERSION;
  environment = environment;

  // Language switcher
  supportedLangs = [
    { code: 'en-US', label: 'English', ready: true },
    { code: 'ms-MY', label: 'Bahasa Melayu', ready: true },
    { code: 'zh-CN', label: '简体中文', ready: true },
  ];

  isActiveLang(code: string): boolean {
    return this.translateService.currentLang === code;
  }

  switchLanguage(lang: string): void {
    this.translateService.use(lang);
    try { localStorage.setItem('pitchin_lang', lang); } catch { /* noop */ }
  }

  form: UntypedFormGroup;
  showFormError: boolean = false;

  constructor(
    private readonly modalService: NgbModal,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly errorService: ErrorService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }

  popupFile(url: number) {
    const popupOptions = {
      size: 'lg',
      centered: true,
      keyboard: false,
      windowClass: 'modal-full-screen',
    } as NgbModalOptions;

    const modalRef = this.modalService.open(TncModalComponent, popupOptions);
    modalRef.componentInstance.url = url;
  }

  buildForm() {
    this.form = this.formBuilder.group({
      email: [
        '',
        Validators.compose([Validators.required, Validators.pattern(DefaultEmailPattern)]),
      ],
    });
  }

  submit() {
    this.showFormError = false;

    if (this.form.valid) {
      this.handleSubscribe(this.form.get('email').value);
    } else {
      this.showFormError = true;
    }
  }

  handleSubscribe(emailAddress: string) {
    if (!emailAddress) return;

    this.accountService.subscribeEmail(emailAddress).subscribe({
      next: (res) => {
        if (res) {
          this.form.reset();
          this.toastService.success(
            this.translateService.instant('footer.subscribeEmailSuccessful'),
          );
        }
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }
}
